import { Agent, DEFAULT_ORGANIZATION_ID, Project } from '@moderepo/mode-internal-apis';
import { UsageMode } from '@moderepo/mode-ui-state';
import { RitzMenuItemName, ProjectUIConfig } from '../../utils/ritzUIExtensionUtils';
import { getOrganization } from '../app/App';
import { RouteHelper } from './RouteHelper';


/**
 * This function returns the UI config for Ritz Projects of UNKNOWN types or projects of KNOWN type that don't have handler for custom project
 * config. For project types that have handler for custom UI config e.g. RobotCloud, there will be a different function that will return
 * a different config.
 *
 * @param loggedInAgent
 * @param project
 */
export const getRitzDefaultProjectUIConfig = (loggedInAgent: Agent | undefined, project: Project | undefined): ProjectUIConfig => {
    const routeHelper = RouteHelper.getInstance();
    const organization = getOrganization();

    return {
        drawerMenu: {
            appNameKey : organization?.id === DEFAULT_ORGANIZATION_ID ? 'app.title_default' : 'app.title',
            projectLogo: '',
            menuItems  : [
                {
                    itemName: RitzMenuItemName.DASHBOARD,
                    disabled: project === undefined,
                    labelKey: 'drawer_menu.dashboard.label',
                    iconKey : 'dashboard',
                    link    : project ? routeHelper.getProjectHomePageUrl(project.id) : undefined,
                },
                {
                    itemName    : RitzMenuItemName.VIEW_DATA,
                    disabled    : project === undefined,
                    labelKey    : 'drawer_menu.view_data.label',
                    iconKey     : 'insights',
                    selectable  : false,
                    subMenuItems: [
                        {
                            itemName: RitzMenuItemName.ENTITIES,
                            disabled: project === undefined,
                            labelKey: 'drawer_menu.reports.label',
                            iconKey : 'summarize',
                            link    : project ? routeHelper.getEntitiesPageUrl(project.id, undefined, UsageMode.VIEW_DATA) : undefined,
                            hidden  : true, // Make this menu hidden by default unless it is configured to be shown in the project's uiSettings
                        },
                    ],
                },
                {
                    itemName: RitzMenuItemName.ALERTS,
                    disabled: project === undefined,
                    labelKey: 'drawer_menu.alerts.label',
                    iconKey : 'notifications_active',
                    link    : project ? routeHelper.getAlertsPageUrl(project.id) : undefined,
                    hidden  : true, // Make this menu hidden by default unless it is configured to be shown in the project's uiSettings
                },
                {
                    itemName    : RitzMenuItemName.MANAGE_DATA,
                    disabled    : project === undefined,
                    labelKey    : 'drawer_menu.manage_data.label',
                    iconKey     : 'construction',
                    selectable  : false,
                    subMenuItems: [
                        {
                            itemName: RitzMenuItemName.HOMES,
                            disabled: project === undefined,
                            labelKey: 'drawer_menu.homes.label',
                            iconKey : 'icons.home',
                            link    : project ? routeHelper.getHomesPageUrl(project.id) : undefined,
                        },
                        {
                            itemName: RitzMenuItemName.ENTITIES,
                            disabled: project === undefined,
                            labelKey: 'drawer_menu.entities.label',
                            iconKey : 'icons.entity',
                            link    : project ? routeHelper.getEntitiesPageUrl(project.id, undefined, UsageMode.MANAGE_DATA) : undefined,
                            hidden  : true, // Make this menu hidden by default unless it is configured to be shown in the project's uiSettings
                        },
                        {
                            itemName: RitzMenuItemName.DEVICES,
                            disabled: project === undefined,
                            labelKey: 'drawer_menu.devices.label',
                            iconKey : 'icons.device',
                            link    : project ? routeHelper.getProjectDevicesPageUrl(project.id) : undefined,
                        },
                        {
                            itemName: RitzMenuItemName.USERS,
                            disabled: project === undefined,
                            labelKey: 'drawer_menu.users.label',
                            iconKey : 'icons.user',
                            link    : project ? routeHelper.getUsersPageUrl(project.id) : undefined,
                        },
                        {
                            itemName: RitzMenuItemName.ALERT_RULES,
                            disabled: project === undefined,
                            labelKey: 'drawer_menu.alert_rules.label',
                            iconKey : 'notifications',
                            link    : project ? routeHelper.getAlertRulesPageUrl(project.id) : undefined,
                            hidden  : true, // Make this menu hidden by default unless it is configured to be shown in the project's uiSettings
                        },
                    ],
                },
                {
                    itemName: RitzMenuItemName.DOWNLOADS,
                    disabled: project === undefined,
                    labelKey: 'drawer_menu.ts_download_request_view.label',
                    iconKey : 'get_app',
                    link    : project ? routeHelper.getDownloadExportedDataPageUrl(project.id) : undefined,
                },
                {
                    itemName : RitzMenuItemName.ADMIN,
                    adminOnly: true,
                    disabled : project === undefined,
                    labelKey : 'drawer_menu.project_settings.label',
                    iconKey  : 'settings',
                    link     : project ? routeHelper.getAdminPageUrl({ projectId: project.id }) : '',
                },
            ],
        },
        projectDashboardPage: {
            subtitleKey: '',
            titleKey   : '',
            components : [
                {
                    compProps: {
                        spacing: 3,
                    },
                    gridItems: [
                        {
                            compProps: {
                                xs: 12,
                            },
                            components: [
                                {
                                    componentName  : 'DashboardsContainer',
                                    routeParamProps: {
                                        projectId: '{{projectId}}',
                                    },
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    };
};
